import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Breadcrumb, Form, message, Select, Modal } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { nanoid } from "nanoid";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import HandoverTimeline from "components/handover-timeline/handover-timeline";
import PropertyForm, { PropertyFormOutput, uploadPropertyFormData } from "components/property-form/property-form";
import HandoverSent from "components/handover-sent/handover-sent";
import InviteOrgModal, { NewOrgData, TEMP_ORG_PREFIX } from "components/invite-org-modal/invite-org-modal";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useGetOrganizationsByWorkspaceIdQuery } from "graphql/queries/get-organizations-by-workspace-id.generated";
import { useGetHandoverByIdQuery } from "graphql/queries/get-handover-by-id.generated";
import { useCreateHandoverMutation } from "graphql/mutations/create-handover.generated";
import { useUpdateHandoverByIdMutation } from "graphql/mutations/update-handover-by-id.generated";
import {
	InputMaybe,
	HandoverInput,
	CreateOrganizationInput,
	CreatePropertyInput,
	UserInvitationInput,
	CreateHandoverOutput
} from "graphql/generated/graphql";
import routeNames from "constants/route-names";
import { OrganizationType, HandoverStatus, UserRoleValue } from "constants/field-values";

const TEMP_PROPERTY_PREFIX = "__temp_property__";

const ADD_NEW_ORG_KEY = "__add_new_org__";
const ADD_NEW_PROPERTY_KEY = "__add_new_property__";

const HandoverAdd = () => {
	const { loggedUserValue } = useLoggedUser();
	const navigate = useNavigate();
	const { handoverId: editHandoverId } = useParams<{ handoverId: string }>();
	const isEditMode = !!editHandoverId;

	const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
	const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
	const [projectName, setProjectName] = useState<string>("");
	const [description, setDescription] = useState<string>("");
	const [additionalNotes, setAdditionalNotes] = useState<string>("");
	const [hasFormSubmitted, setHasFormSubmitted] = useState(false);
	const [createdHandover, setCreatedHandover] = useState<CreateHandoverOutput | null>(null);
	const [isInviteOrgModalOpen, setIsInviteOrgModalOpen] = useState(false);
	const [isAddPropertyModalOpen, setIsAddPropertyModalOpen] = useState(false);
	const [newOrgData, setNewOrgData] = useState<NewOrgData | null>(null);

	const [newPropertyFormOutput, setNewPropertyFormOutput] = useState<PropertyFormOutput | null>(null);
	const [newPropertyTempId] = useState<string>(`${TEMP_PROPERTY_PREFIX}${nanoid()}`);

	const [newUserData, setNewUserData] = useState<{ email: string } | null>(null);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [editInitialized, setEditInitialized] = useState(false);

	const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
	const loggedUserIsCustomer = loggedUserValue?.organization?.type === OrganizationType.Customer;
	const orgLabel = loggedUserIsCustomer ? "vendor" : "customer";
	const canAddProperty = [
		UserRoleValue.admin_user,
		UserRoleValue.vendor_manager,
		UserRoleValue.vendor_employee,
		UserRoleValue.customer_manager
	].includes(loggedUserValue?.role as UserRoleValue);

	const handleOnCompleteGetOrganizationsByWorkspaceId = useCallback(_data => {
		console.log("Complete Get Organizations By WorkspaceId data: ", _data);
	}, []);

	const handleOnErrorGetOrganizationsByWorkspaceId = useCallback((error: ApolloError) => {
		console.log("Error Get Organizations By WorkspaceId Error: ", error);
	}, []);

	const { data: organizationsByWorkspaceData, loading: organizationsByWorkspaceLoading } =
		useGetOrganizationsByWorkspaceIdQuery({
			skip: !loggedUserValue.workspace_id || !loggedUserValue.organization,
			variables: {
				workspace_id: loggedUserValue.workspace_id,
				org_types: loggedUserIsCustomer ? [OrganizationType.Vendor] : [OrganizationType.Customer]
			},
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetOrganizationsByWorkspaceId,
			onCompleted: handleOnCompleteGetOrganizationsByWorkspaceId
		});

	const handleOnCompleteGetPropertiesByOrganizationId = useCallback(data => {
		console.log("handleOnErrorOrganizations Complete data: ", data);
	}, []);

	const handleOnErrorGetPropertiesByOrganizationId = useCallback((error: ApolloError) => {
		console.log("useGetPropertiesByOrganizationIdQuery Error: ", error);
	}, []);

	// Customer path → the properties belong to the user's own org
	// Vendor path → the properties belong to the selected client's org
	const propertiesQueryOrgId = loggedUserIsCustomer ? loggedUserValue?.organization_id : selectedOrgId;

	const { data: propertiesByOrganizationData, loading: loadingPropertiesByOrganization } =
		useGetPropertiesByOrganizationIdQuery({
			skip: !propertiesQueryOrgId || propertiesQueryOrgId.startsWith(TEMP_ORG_PREFIX),
			variables: {
				organization_id: propertiesQueryOrgId
			},
			errorPolicy: "none",
			fetchPolicy: "cache-first",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetPropertiesByOrganizationId,
			onCompleted: handleOnCompleteGetPropertiesByOrganizationId
		});

	const handleOnCompleteCreateHandover = useCallback(
		data => {
			console.log("Complete CreateHandover : ", data);

			// Backend may skip the user invitation if the email is already in use.
			// Surface that to the user without blocking the success flow.
			const skippedReason = data?.create_handover?.user_invitation_skipped_reason;
			if (skippedReason) {
				message.info(skippedReason);
			}

			if (loggedUserIsCustomer) {
				setCreatedHandover(data.create_handover);
				setHasFormSubmitted(true);
			} else {
				navigate(routeNames.HANDOVER_DETAILS.replace(":handoverId", data.create_handover.handover_id));
			}
		},
		[loggedUserIsCustomer, navigate]
	);

	const handleOnErrorCreateHandover = useCallback((error: ApolloError) => {
		console.log("Error CreateHandover error: ", error);
		const serverMessage = error.graphQLErrors?.[0]?.message ?? error.message;
		message.error(serverMessage || "Failed to create Handover. Please try again.");
		console.error("Error in onFinish:", error);
	}, []);

	const [createHandoverMutation, { loading: mutationLoading }] = useCreateHandoverMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorCreateHandover,
		onCompleted: handleOnCompleteCreateHandover
	});

	const { data: editHandoverData, loading: editHandoverLoading } = useGetHandoverByIdQuery({
		variables: { id: editHandoverId!, isAnon: false },
		skip: !editHandoverId,
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only"
	});

	const handleOnErrorUpdateHandover = useCallback((error: ApolloError) => {
		console.error("Error updating handover:", error);
		message.error("Failed to update Handover. Please try again.");
	}, []);

	const [updateHandoverByIdMutation, { loading: updateMutationLoading }] = useUpdateHandoverByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdateHandover,
		refetchQueries: ["getHandoverById"]
	});

	useEffect(() => {
		if (!isEditMode || !editHandoverData?.handover_by_pk || editInitialized) return;
		const h = editHandoverData.handover_by_pk;
		setProjectName(h.project_name ?? "");
		setDescription(h.project_description ?? "");
		setAdditionalNotes(h.requester_notes ?? "");
		const counterpartyOrgId = loggedUserIsCustomer
			? (h.vendor_organization_id ?? null)
			: (h.customer_organization_id ?? null);
		setSelectedOrgId(counterpartyOrgId);
		setSelectedPropertyId(h.property_id ?? null);
		setEditInitialized(true);
	}, [isEditMode, editHandoverData, editInitialized, loggedUserIsCustomer]);

	const handleProjectNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setProjectName(e.target.value);
	}, []);

	const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setDescription(e.target.value);
	}, []);

	const handleAdditionalNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setAdditionalNotes(e.target.value);
	}, []);

	const handleInviteOrgSave = useCallback(
		({
			tempId,
			orgName,
			orgEmail,
			userEmail
		}: {
			tempId: string;
			orgName: string;
			orgEmail: string;
			userEmail: string;
		}) => {
			setNewOrgData({ tempId, name: orgName, email: orgEmail });
			setSelectedOrgId(tempId);
			setNewUserData(userEmail.trim() ? { email: userEmail.trim() } : null);
			setIsInviteOrgModalOpen(false);
		},
		[]
	);

	const handleInviteOrgCancel = useCallback(() => {
		setIsInviteOrgModalOpen(false);
	}, []);

	const handleEditNewOrg = useCallback(() => {
		setIsInviteOrgModalOpen(true);
	}, []);

	const handleRemoveNewOrg = useCallback(() => {
		setNewOrgData(null);
		setSelectedOrgId(null);
		setNewUserData(null);
	}, []);

	const handleNewPropertySave = useCallback(
		(output: PropertyFormOutput) => {
			setNewPropertyFormOutput(output);
			setSelectedPropertyId(newPropertyTempId);
			setIsAddPropertyModalOpen(false);
		},
		[newPropertyTempId]
	);

	const handleAddPropertyCancel = useCallback(() => {
		setIsAddPropertyModalOpen(false);
		// If the user cancels without ever saving, clear the temp selection
		if (selectedPropertyId === newPropertyTempId && !newPropertyFormOutput) {
			setSelectedPropertyId(null);
		}
	}, [selectedPropertyId, newPropertyTempId, newPropertyFormOutput]);

	const handleRemoveNewProperty = useCallback(() => {
		setNewPropertyFormOutput(null);
		setSelectedPropertyId(null);
	}, []);

	const onFinish = useCallback(async () => {
		setIsSubmitting(true);

		if (isEditMode && editHandoverId) {
			try {
				await updateHandoverByIdMutation({
					variables: {
						id: editHandoverId,
						set: {
							project_name: projectName,
							project_description: description,
							requester_notes: additionalNotes
						}
					}
				});
				navigate(routeNames.HANDOVER_DETAILS.replace(":handoverId", editHandoverId));
			} catch (err) {
				console.error("Error updating handover:", err);
				message.error("Failed to update Handover. Please try again.");
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		try {
			// The logged-in user's org is always on their own side of the handover.
			// The counterparty org comes from the dropdown (selectedOrgId).
			// If the counterparty is a new org (tempId), its real id will be resolved by the backend via new_organization.
			const selectedCounterpartyId = selectedOrgId === newOrgData?.tempId ? null : selectedOrgId;
			const customer_organization_id = loggedUserIsCustomer ? loggedUserValue?.organization_id : selectedCounterpartyId;
			const vendor_organization_id = loggedUserIsCustomer ? selectedCounterpartyId : loggedUserValue?.organization_id;

			let new_organization: InputMaybe<CreateOrganizationInput> = null;
			let new_property: InputMaybe<CreatePropertyInput> = null;
			let new_user: InputMaybe<UserInvitationInput> = null;

			if (newOrgData) {
				new_organization = {
					name: newOrgData.name,
					email: newOrgData.email,
					type: loggedUserIsCustomer ? OrganizationType.Vendor : OrganizationType.Customer,
					workspace_id: loggedUserValue?.workspace_id,
					created_by_user_id: loggedUserValue?.id,
					display_price: false,
					organizationImageURL: null
				};
			}

			if (newUserData) {
				new_user = {
					email: newUserData.email,
					workspace_id: loggedUserValue?.workspace_id
				};
			}

			const isNewProperty = selectedPropertyId === newPropertyTempId;
			const property_id = isNewProperty ? null : selectedPropertyId;

			if (isNewProperty && newPropertyFormOutput) {
				new_property = await uploadPropertyFormData(newPropertyFormOutput, loggedUserValue);
			}

			const handoverInput: HandoverInput = {
				base_url: baseUrl,
				// Customer path → PENDING_VENDOR (vendor must complete)
				// Vendor path → VENDOR_DRAFT (vendor is preparing before notifying customer)
				status: loggedUserIsCustomer ? HandoverStatus.PENDING_VENDOR : HandoverStatus.VENDOR_DRAFT,
				created_by_user_id: loggedUserValue?.id,
				workspace_id: loggedUserValue?.workspace_id,
				is_customer_path: loggedUserIsCustomer,
				property_id,
				customer_organization_id,
				vendor_organization_id,
				project_name: projectName,
				project_description: description,
				requester_notes: additionalNotes,
				new_organization,
				new_property,
				new_user
			};

			createHandoverMutation({ variables: { input: handoverInput } });
		} catch (err) {
			console.error("Error in onFinish:", err);
			message.error("Failed to create Handover. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	}, [
		loggedUserValue,
		loggedUserIsCustomer,
		baseUrl,
		selectedPropertyId,
		newPropertyTempId,
		newPropertyFormOutput,
		selectedOrgId,
		projectName,
		description,
		additionalNotes,
		newOrgData,
		newUserData,
		createHandoverMutation,
		isEditMode,
		editHandoverId,
		updateHandoverByIdMutation
	]);

	const propertyOptions = useMemo(() => {
		let existingOptions;

		if (loggedUserIsCustomer) {
			existingOptions =
				loggedUserValue?.user_properties?.map(up => ({
					value: up.property?.id,
					label: up.property?.name
				})) || [];
		} else {
			existingOptions =
				propertiesByOrganizationData?.properties.map(property => ({
					value: property?.id,
					label: property?.name
				})) || [];
		}

		// Show the staged new property as a selectable option
		const newPropertyOption = newPropertyFormOutput
			? [{ value: newPropertyTempId, label: newPropertyFormOutput.name }]
			: [];

		const result = [...existingOptions, ...newPropertyOption];

		if (canAddProperty) {
			result.push({ value: ADD_NEW_PROPERTY_KEY, label: "Add Property" });
		}

		console.log("propertyOptions result: ", result);
		return result;
	}, [
		propertiesByOrganizationData,
		newPropertyFormOutput,
		newPropertyTempId,
		loggedUserIsCustomer,
		loggedUserValue?.user_properties,
		canAddProperty
	]);

	const orgOptionsWithEmail = useMemo(() => {
		const { organizations = [] } = organizationsByWorkspaceData || {};

		return [
			...organizations.map(org => ({
				value: org.id,
				label: org.name,
				email: org.email
			})),
			...(newOrgData ? [{ value: newOrgData.tempId, label: newOrgData.name, email: newOrgData.email }] : []),
			{
				value: ADD_NEW_ORG_KEY,
				label: `Invite and assign a new ${orgLabel}`,
				email: null
			}
		];
	}, [orgLabel, organizationsByWorkspaceData, newOrgData]);

	const isFormValid = isEditMode ? !!projectName : selectedPropertyId && selectedOrgId && projectName;
	const isLoading = isSubmitting || mutationLoading || updateMutationLoading;

	const orgSelect = (
		<Select
			className="ha-select"
			placeholder={`Select ${orgLabel.charAt(0).toUpperCase() + orgLabel.slice(1)}`}
			value={selectedOrgId || undefined}
			onSelect={(value: string) => {
				if (value === ADD_NEW_ORG_KEY) {
					setSelectedOrgId(null);
					setIsInviteOrgModalOpen(true);
					return;
				}

				// if it's a vendor path reset property selection whenever the org changes to avoid mismatches
				// in the customer path properties don't depend on the selected vendor's organization
				if (!loggedUserIsCustomer) {
					setSelectedPropertyId(null);
					setNewPropertyFormOutput(null);
				}

				setSelectedOrgId(value);

				// Selecting an existing org discards any manually-entered new org
				if (newOrgData && value !== newOrgData.tempId) {
					setNewOrgData(null);
					message.info(`New ${orgLabel} replaced with existing selection`);
				}
			}}
			showSearch
			loading={organizationsByWorkspaceLoading}
			filterOption={(input, option) => {
				if (option?.value === ADD_NEW_ORG_KEY) return true;
				return ((option?.label as string) || "").toLowerCase().includes(input.toLowerCase());
			}}
			popupClassName="ha-select-dropdown"
			popupMatchSelectWidth={false}
			optionRender={option => {
				if (option.value === ADD_NEW_ORG_KEY) {
					return (
						<span className="ha-dropdown-item ha-invite-vendor-item">
							<span>+</span>
							{option.label}
						</span>
					);
				}

				const orgData = orgOptionsWithEmail.find(v => v.value === option.value);
				return (
					<span className="ha-dropdown-item">
						{option.label}
						{orgData?.email && (
							<>
								, <span className="ha-vendor-email">{orgData.email}</span>
								{newOrgData && option.value === newOrgData?.tempId ? (
									<span className="ha-new-org-tag-badge select">New</span>
								) : null}
							</>
						)}
					</span>
				);
			}}
			options={orgOptionsWithEmail.map(v => ({ value: v.value, label: v.label }))}
			disabled={isEditMode || (loggedUserIsCustomer ? !selectedPropertyId : false)}
		/>
	);

	const propertySelect = (
		<Select
			className="ha-select"
			placeholder="Select Property"
			value={selectedPropertyId || undefined}
			onSelect={(value: string) => {
				if (value === ADD_NEW_PROPERTY_KEY && canAddProperty) {
					setSelectedPropertyId(null);
					setIsAddPropertyModalOpen(true);
					return;
				}
				setSelectedPropertyId(value);
			}}
			options={propertyOptions}
			showSearch
			loading={loadingPropertiesByOrganization}
			disabled={
				isEditMode ||
				(loggedUserIsCustomer ? loadingPropertiesByOrganization : !selectedOrgId || loadingPropertiesByOrganization)
			}
			filterOption={(input, option) => {
				if (option?.value === ADD_NEW_PROPERTY_KEY) return true;
				return ((option?.label as string) || "").toLowerCase().includes(input.toLowerCase());
			}}
			popupClassName="ha-select-dropdown"
			popupMatchSelectWidth={false}
			optionRender={option => {
				if (option.value === ADD_NEW_PROPERTY_KEY) {
					return (
						<span className="ha-dropdown-item ha-invite-vendor-item">
							<span>+</span>
							{option.label}
						</span>
					);
				}
				if (option.value === newPropertyTempId) {
					return (
						<span className="ha-dropdown-item">
							{option.label}
							<span className="ha-new-org-tag-badge select">New</span>
						</span>
					);
				}
				return <span className="ha-dropdown-item">{option.label}</span>;
			}}
		/>
	);

	if (isEditMode && editHandoverLoading && !editInitialized) return <LoadingOverlay />;

	return (
		<div className="ha-screen-container">
			<Breadcrumb
				className="products-breadcrumbs"
				items={[
					{
						title: "My Handovers",
						onClick: () => navigate(routeNames.HANDOVERS)
					},
					{
						title: isEditMode ? "Edit Handover" : "New Handover",
						...(isEditMode && editHandoverId
							? { onClick: () => navigate(routeNames.HANDOVER_DETAILS.replace(":handoverId", editHandoverId)) }
							: {})
					}
				]}
			/>

			<Form layout="vertical" className="ha-form-container">
				<div className="ha-timeline-container">
					<HandoverTimeline handover={createdHandover?.handover} />
				</div>

				{hasFormSubmitted ? (
					<HandoverSent handover={createdHandover?.handover} />
				) : (
					<div className="ha-card">
						<div className="ha-fields-container">
							<div className="ha-dropdowns-container">
								{loggedUserIsCustomer ? (
									<>
										{propertySelect}
										{orgSelect}
									</>
								) : (
									<>
										{orgSelect}
										{propertySelect}
									</>
								)}
							</div>

							<div className="ha-field-group">
								<label className="ha-label" htmlFor="project-name">
									Project name
								</label>
								<input
									id="project-name"
									className="ha-input"
									placeholder="e.g., Access Control system installation"
									value={projectName}
									onChange={handleProjectNameChange}
								/>
							</div>

							<div className="ha-field-group">
								<label className="ha-label" htmlFor="guide-description">
									Description (optional)
								</label>
								<textarea
									id="guide-description"
									className="ha-textarea"
									placeholder="e.g., Installed a new PDK access control system, including 12 new readers and 100 new FOBs."
									value={description}
									onChange={handleDescriptionChange}
									rows={4}
								/>
							</div>

							<div className="ha-field-group">
								<label className="ha-label" htmlFor="additional-notes">
									Additional Notes (optional)
								</label>
								<textarea
									id="additional-notes"
									className="ha-textarea"
									placeholder="e.g. As we discussed during our walkthrough, we can also provide virtual FOBs so that residents can use their phones to unlock doors."
									value={additionalNotes}
									onChange={handleAdditionalNotesChange}
									rows={4}
								/>
							</div>
						</div>

						{/* New org confirmation summary — hidden in edit mode */}
						{!isEditMode && newOrgData && (
							<>
								<div className="ha-org-summary-divider" />
								<div className="ha-field-group org">
									<label className="ha-label">New Organization</label>
									<div className="ha-org-summary">
										<div className="ha-org-summary-body">
											{/* Organization section */}
											<div className="ha-org-summary-section">
												<div className="ha-summary-row">
													<span className="ha-summary-key">Company:</span>
													<span className="ha-summary-value">
														{newOrgData.name}
														<span className="ha-new-org-tag-badge">New</span>
													</span>
												</div>
												<div className="ha-summary-row">
													<span className="ha-summary-key">Organization email:</span>
													<span className="ha-summary-value">{newOrgData.email}</span>
												</div>
											</div>

											{/* Divider + User invitation section */}
											{newUserData && (
												<div className="ha-org-summary-section">
													<div className="ha-summary-row">
														<span className="ha-summary-key">User invitation:</span>
														<span className="ha-summary-value">{newUserData.email}</span>
													</div>
												</div>
											)}
										</div>

										{/* Actions — top-right */}
										<div className="ha-new-org-tag-actions ha-org-summary-actions">
											<button type="button" className="ha-new-org-tag-btn" onClick={handleEditNewOrg} title="Edit">
												<img src={process.env.PUBLIC_URL + "/assets/images/icons/edit-pencil.svg"} alt="Edit" />
											</button>
											<button type="button" className="ha-new-org-tag-btn" onClick={handleRemoveNewOrg} title="Remove">
												<img src={process.env.PUBLIC_URL + "/assets/images/icons/remove-trash.svg"} alt="Remove" />
											</button>
										</div>
									</div>
								</div>
							</>
						)}

						{!isEditMode && newPropertyFormOutput && selectedPropertyId === newPropertyTempId && (
							<>
								<div className="ha-org-summary-divider" />
								<div className="ha-field-group org">
									<label className="ha-label">New Property</label>
									<div className="ha-org-summary">
										<div className="ha-org-summary-body">
											<div className="ha-org-summary-section">
												<div className="ha-summary-row">
													<span className="ha-summary-key">Property name:</span>
													<span className="ha-summary-value">
														{newPropertyFormOutput.name}
														<span className="ha-new-org-tag-badge">New</span>
													</span>
												</div>
												{newPropertyFormOutput.address && (
													<div className="ha-summary-row">
														<span className="ha-summary-key">Address:</span>
														<span className="ha-summary-value">{newPropertyFormOutput.address}</span>
													</div>
												)}
												{newPropertyFormOutput.buildingType && (
													<div className="ha-summary-row">
														<span className="ha-summary-key">Building type:</span>
														<span className="ha-summary-value">{newPropertyFormOutput.buildingType}</span>
													</div>
												)}
											</div>

											{(newPropertyFormOutput.contactName ||
												newPropertyFormOutput.contactEmail ||
												newPropertyFormOutput.contactPhoneNumber) && (
												<>
													<div className="ha-org-summary-divider" />
													<div className="ha-org-summary-section">
														{newPropertyFormOutput.contactName && (
															<div className="ha-summary-row">
																<span className="ha-summary-key">Contact:</span>
																<span className="ha-summary-value">{newPropertyFormOutput.contactName}</span>
															</div>
														)}
														{newPropertyFormOutput.contactEmail && (
															<div className="ha-summary-row">
																<span className="ha-summary-key">Contact email:</span>
																<span className="ha-summary-value">{newPropertyFormOutput.contactEmail}</span>
															</div>
														)}
														{newPropertyFormOutput.contactPhoneNumber && (
															<div className="ha-summary-row">
																<span className="ha-summary-key">Contact phone:</span>
																<span className="ha-summary-value">{newPropertyFormOutput.contactPhoneNumber}</span>
															</div>
														)}
													</div>
												</>
											)}
										</div>

										{/* Actions — top-right */}
										<div className="ha-new-org-tag-actions ha-org-summary-actions">
											<button
												type="button"
												className="ha-new-org-tag-btn"
												onClick={() => setIsAddPropertyModalOpen(true)}
												title="Edit"
											>
												<img src={process.env.PUBLIC_URL + "/assets/images/icons/edit-pencil.svg"} alt="Edit" />
											</button>
											<button
												type="button"
												className="ha-new-org-tag-btn"
												onClick={handleRemoveNewProperty}
												title="Remove"
											>
												<img src={process.env.PUBLIC_URL + "/assets/images/icons/remove-trash.svg"} alt="Remove" />
											</button>
										</div>
									</div>
								</div>
							</>
						)}

						<div className="ha-actions-row">
							<button type="button" className="highnote-button" disabled={!isFormValid || isLoading} onClick={onFinish}>
								{isLoading ? "Submitting..." : "Continue"}
							</button>
						</div>
					</div>
				)}
			</Form>

			<InviteOrgModal
				open={isInviteOrgModalOpen}
				orgLabel={orgLabel}
				initialValues={
					newOrgData
						? {
								tempId: newOrgData.tempId,
								orgName: newOrgData.name,
								orgEmail: newOrgData.email,
								userEmail: newUserData?.email ?? ""
							}
						: undefined
				}
				onCancel={handleInviteOrgCancel}
				onSave={handleInviteOrgSave}
			/>

			<Modal
				open={isAddPropertyModalOpen}
				onCancel={handleAddPropertyCancel}
				footer={null}
				title={null}
				closable={false}
				className="ha-invite-org-modal"
				width={680}
				/*
				 We want the form to persist its internal state while the modal is closed
				 so the user can re-open and continue editing.
				 */
				destroyOnClose={false}
			>
				<div className="ha-modal-content">
					<div className="ha-modal-header">
						<span className="ha-modal-title">Add a new property</span>
					</div>
					<PropertyForm
						key={newPropertyFormOutput ? "edit" : "create"}
						initialValues={newPropertyFormOutput ?? undefined}
						submitLabel="Save"
						onSubmit={handleNewPropertySave}
						extraOrganizations={newOrgData ? [{ id: newOrgData.tempId, name: `${newOrgData.name} (New)` }] : []}
						lockedOrganizationId={loggedUserIsCustomer ? loggedUserValue?.organization_id : selectedOrgId || undefined}
						renderExtraActions={() => (
							<button type="button" className="ha-cancel-btn" onClick={handleAddPropertyCancel}>
								Cancel
							</button>
						)}
						isHandoverForm
					/>
				</div>
			</Modal>
		</div>
	);
};

export default HandoverAdd;
